import { onlineManager } from '@tanstack/react-query'
import posthog from 'posthog-js'
import React, { useState } from 'react'
import { useMount } from 'react-use'

import OverlayCard from '@/components/misc/OverlayCard'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/heading'
import { useAuth } from '@/hooks/useAuth'
import { useCurrentCompany } from '@/hooks/useCurrentCompany'
import { useCurrentGroup } from '@/hooks/useCurrentGroup'
import { useCurrentNetwork } from '@/hooks/useCurrentNetwork'
import useDialog from '@/hooks/useDialog'
import { useSessionMetadata } from '@/hooks/useSessionMetadata'
import {
  CLIENT_CHANGED,
  COMPANY_CHANGED,
  ContextChangeMessage,
  GROUP_CHANGED,
  NETWORK_CHANGED,
  USER_CHANGED,
  useUserContextChanged,
} from '@/hooks/useUserContextChanged'
import { POSTHOG_EVENT } from '@/lib/constants'
import authService from '@/lib/services/authService'
import sentryService from '@/lib/services/sentryService'

// The session is in exactly one of company, group, network or client scope, so
// a change to any of them is a change to the same thing.
const SCOPE_CHANGE_TYPES = [
  COMPANY_CHANGED,
  GROUP_CHANGED,
  NETWORK_CHANGED,
  CLIENT_CHANGED,
]

function getContextAxis(messageType: string) {
  return SCOPE_CHANGE_TYPES.includes(messageType) ? 'scope' : messageType
}

const UserChangedContextWarning: React.FC = () => {
  const sessionMetadata = useSessionMetadata()
  const isOnline = onlineManager.isOnline()
  const { isClientUser, currentUser } = useAuth()
  const currentCompany = useCurrentCompany()
  const currentGroup = useCurrentGroup()
  const currentNetwork = useCurrentNetwork()
  const { isShowingDialog, showDialog, hideDialog } = useDialog()
  const [activeMessage, setActiveMessage] =
    useState<ContextChangeMessage | null>(null)

  // Raise the conflict modal and pause React Query so it stops
  // retrying/refetching and holds mutations paused - otherwise requests keep
  // replaying against the now-switched server-side context while the modal is
  // up. Paired with clearConflict, which resumes React Query.
  function raiseConflict(message: ContextChangeMessage | null) {
    onlineManager.setOnline(false)
    setActiveMessage(message)
    showDialog()
  }

  // Dismiss the modal and resume React Query, respecting the real connection
  // state rather than forcing online.
  function clearConflict() {
    setActiveMessage(null)
    hideDialog()
    onlineManager.setOnline(navigator.onLine)
  }

  // The apiService interceptor detects a background context switch from the
  // API's response headers and fires this event. Show the same conflict modal so
  // the user can refresh to re-bootstrap against the new server-side context.
  useMount(() => {
    authService.addEventListener('sessionContextConflict', () => {
      raiseConflict(null)
    })
  })

  useUserContextChanged((message) => {
    // A "matching" broadcast may only dismiss a modal that was raised for the
    // SAME context axis (company/group/network/client share one axis, so switching
    // company then back to this tab's group dismisses the modal). Otherwise an
    // unrelated message dismisses a conflict it says nothing about - e.g. after switching company in another tab, that
    // tab re-bootstraps and broadcasts a same-user USER_CHANGED, which would
    // otherwise hide the company-conflict modal here (it flashes for ~1s and
    // vanishes). Interceptor-raised modals (activeMessage === null) are never
    // dismissed by a broadcast and stay until the user refreshes.
    const dismissMatchingConflict = () => {
      if (
        activeMessage &&
        getContextAxis(activeMessage.type) === getContextAxis(message.type)
      ) {
        clearConflict()
      }
    }

    switch (message.type) {
      case COMPANY_CHANGED:
        if (currentCompany?.id === message.payload.company?.id) {
          dismissMatchingConflict()
        } else {
          raiseConflict(message)
        }
        break

      case GROUP_CHANGED:
        if (currentGroup?.id === message.payload.group?.id) {
          dismissMatchingConflict()
        } else {
          raiseConflict(message)
        }
        break

      case NETWORK_CHANGED:
        if (currentNetwork?.id === message.payload.network?.id) {
          dismissMatchingConflict()
        } else {
          raiseConflict(message)
        }
        break

      case CLIENT_CHANGED:
        if (isClientUser) {
          dismissMatchingConflict()
        } else {
          raiseConflict(message)
        }
        break

      case USER_CHANGED:
        // If this tab hasn't finished loading its own user yet, there's
        // nothing to compare against - ignore rather than risk a false
        // positive against another tab that's also still initialising.
        if (!currentUser || currentUser.id === message.payload.user?.id) {
          dismissMatchingConflict()
        } else {
          raiseConflict(message)
        }
        break

      default:
        raiseConflict(activeMessage)
        console.error('Unknown context change')
    }
  })

  if (isShowingDialog) {
    const logData: Record<string, unknown> = {
      isOnline,
      isClientUser,
      currentCompany,
      currentGroup,
      currentNetwork,
      activeMessage,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      currentUrl: window.location.href,
    }

    console.log('Capturing session conflict shown to user event', logData)

    sentryService.captureMessage({
      messageId: 'session-conflict-shown-to-user',
      message: 'Session conflict shown to user',
      extra: { sessionMetadata, ...logData },
    })

    posthog.capture(POSTHOG_EVENT.sessionConflictShownToUser, {
      isOnline,
    })

    return (
      <OverlayCard className="mt-5 sm:mt-18" variant="dark" fullScreen>
        <Heading size={2}>Session conflict detected</Heading>

        <p className="mb-4">
          It appears that you are logged in as a different company, group,
          network or user in another browser window. Please refresh the page to
          proceed.
        </p>

        <Button
          variant="primary"
          size="lg"
          onClick={() => window.location.reload()}
        >
          Refresh
        </Button>
      </OverlayCard>
    )
  }

  return null
}

export default UserChangedContextWarning
